import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { Badge, Button, EmptyState, Field, Input, Spinner } from '../components/ui';
import { Notice, Page, PageHeader, Section, Stack } from '../components/Page';
import type { ContrastPairDto, ThemeDto, ThemeTokensDto } from '../lib/types';

const DEFAULT_TOKENS: ThemeTokensDto = {
  primary: '#4f46e5', primaryHover: '#4338ca', onPrimary: '#ffffff',
  page: '#f1f5f9', surface: '#ffffff', surfaceMuted: '#f8fafc', surfaceRaised: '#ffffff',
  text: '#0f172a', textMuted: '#64748b',
  border: '#8f8f8f', borderSubtle: '#e2e8f0', focus: '#2563eb',
  danger: '#b91c1c', dangerSoft: '#fef2f2',
  fontFamily: 'system-ui, Segoe UI, sans-serif',
  fontSizePx: 16, radiusPx: 8, maxWidthPx: 680,
};

/**
 * The theme editor.
 *
 * Contrast is checked as colours change rather than only on save. A checker that speaks
 * only at the end gets treated as an obstacle to work around; one that shows the ratio
 * while somebody is still choosing gets treated as advice.
 */
export function Themes() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ThemeDto | 'new' | null>(null);

  const themes = useQuery({ queryKey: ['themes'], queryFn: () => api.themes.list() });

  const remove = useMutation({
    mutationFn: (id: string) => api.themes.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['themes'] }),
  });

  if (themes.isLoading) return <div className="p-8"><Spinner /></div>;

  const data = themes.data?.data ?? [];

  if (editing) {
    return (
      <ThemeEditor
        theme={editing === 'new' ? null : editing}
        onDone={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['themes'] });
        }}
      />
    );
  }

  return (
    <Page width="wide">
      <PageHeader
        icon="sparkle"
        title="Themes"
        description="Colours, type and shape for the forms your respondents see. A theme that
                     would make its own text unreadable cannot be saved."
        actions={
          <Button variant="primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={14} /> New theme
          </Button>
        }
      />

      {data.length === 0 && (
        <EmptyState
          title="No themes yet"
          description="Forms use the built-in look until you create one."
          action={<Button variant="primary" onClick={() => setEditing('new')}>Create a theme</Button>}
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {data.map((theme) => (
          <button
            key={theme.id}
            type="button"
            onClick={() => setEditing(theme)}
            className="rounded-xl border border-[var(--ff-border)] bg-[var(--ff-surface)]
                       p-4 text-left transition-all hover:border-[var(--ff-border-strong)]
                       hover:shadow-[var(--ff-shadow-lg)]"
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <span className="truncate font-medium">{theme.name}</span>
              {theme.isDefault && <Badge tone="primary">default</Badge>}
            </div>

            <Swatches tokens={theme.tokens} />

            <div className="mt-3 flex items-center justify-between text-xs
                            text-[var(--ff-text-muted)]">
              <span>{theme.formCount} form{theme.formCount === 1 ? '' : 's'}</span>
              <span
                role="button"
                tabIndex={0}
                className="text-[var(--ff-danger)] hover:underline"
                onClick={(e) => { e.stopPropagation(); remove.mutate(theme.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); remove.mutate(theme.id); } }}
              >
                Delete
              </span>
            </div>
          </button>
        ))}
      </div>
    </Page>
  );
}

function Swatches({ tokens }: { tokens: ThemeTokensDto }) {
  const shown: (keyof ThemeTokensDto)[] = ['primary', 'surface', 'text', 'border', 'danger'];

  return (
    <div className="flex gap-1.5">
      {shown.map((key) => (
        <span
          key={key}
          title={`${key}: ${tokens[key]}`}
          className="h-7 flex-1 rounded-md border border-[var(--ff-border)]"
          style={{ background: String(tokens[key]) }}
        />
      ))}
    </div>
  );
}

function ThemeEditor({ theme, onDone }: { theme: ThemeDto | null; onDone: () => void }) {
  const [name, setName] = useState(theme?.name ?? 'New theme');
  const [tokens, setTokens] = useState<ThemeTokensDto>(theme?.tokens ?? DEFAULT_TOKENS);
  const [isDefault, setIsDefault] = useState(theme?.isDefault ?? false);
  const [error, setError] = useState<string | null>(null);

  // Debounced, because the check is a round trip and a colour picker fires continuously
  // as it is dragged.
  const [checked, setChecked] = useState<{
    valid: boolean;
    contrast: ContrastPairDto[];
    problems: { path: string; code: string; message: string }[];
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const result = await api.themes.preview({ name, tokens });
        setChecked(result);
      } catch {
        // A failed check must not block editing; save will report it properly.
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [tokens, name]);

  const save = useMutation({
    mutationFn: () => theme
      ? api.themes.update(theme.id, { name, tokens, isDefault })
      : api.themes.create({ name, tokens, isDefault }),
    onSuccess: onDone,
    onError: (e: Error) => setError(e.message),
  });

  const set = <K extends keyof ThemeTokensDto>(key: K, value: ThemeTokensDto[K]) =>
    setTokens((current) => ({ ...current, [key]: value }));

  const blocking = useMemo(
    () => (checked?.contrast ?? []).filter((p) => p.blocking && !p.passes),
    [checked],
  );

  return (
    <Page width="wide">
      <PageHeader
        title={theme ? `Editing ${theme.name}` : 'New theme'}
        description="Changes preview live. Contrast is measured as you go."
        actions={
          <>
            <Button onClick={onDone}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || checked?.valid === false}
            >
              {save.isPending ? 'Saving…' : 'Save theme'}
            </Button>
          </>
        }
      />

      <Stack>
        {error && <Notice tone="danger">{error}</Notice>}

        {blocking.length > 0 && (
          <Notice tone="danger">
            <strong>This cannot be saved yet.</strong>{' '}
            {blocking.map((p) => `${p.label} is ${p.ratio.toFixed(2)}:1, below ${p.required}:1.`).join(' ')}
          </Notice>
        )}

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-5">
            <Section title="Identity">
              <Field label="Theme name">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>

              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--ff-primary)]"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
                Use for forms that have no theme of their own
              </label>
            </Section>

            <Section title="Colour" hint="Hex values only. Anything else is refused before it can reach a stylesheet.">
              <div className="grid gap-3 sm:grid-cols-2">
                <ColourField label="Brand" value={tokens.primary} onChange={(v) => set('primary', v)} />
                <ColourField label="Brand, hovered" value={tokens.primaryHover} onChange={(v) => set('primaryHover', v)} />
                <ColourField label="Text on brand" value={tokens.onPrimary} onChange={(v) => set('onPrimary', v)} />
                <ColourField label="Page background" value={tokens.page} onChange={(v) => set('page', v)} />
                <ColourField label="Form background" value={tokens.surface} onChange={(v) => set('surface', v)} />
                <ColourField label="Body text" value={tokens.text} onChange={(v) => set('text', v)} />
                <ColourField label="Help text" value={tokens.textMuted} onChange={(v) => set('textMuted', v)} />
                <ColourField label="Field border" value={tokens.border} onChange={(v) => set('border', v)} />
                <ColourField label="Focus ring" value={tokens.focus} onChange={(v) => set('focus', v)} />
                <ColourField label="Errors" value={tokens.danger} onChange={(v) => set('danger', v)} />
              </div>
            </Section>

            <Section title="Type and shape">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Font stack" hint="Letters, digits, spaces, hyphens and commas.">
                  <Input value={tokens.fontFamily} onChange={(e) => set('fontFamily', e.target.value)} />
                </Field>
                <Field label="Base font size (px)">
                  <Input type="number" min={12} max={24} value={tokens.fontSizePx}
                         onChange={(e) => set('fontSizePx', Number(e.target.value))} />
                </Field>
                <Field label="Corner radius (px)">
                  <Input type="number" min={0} max={32} value={tokens.radiusPx}
                         onChange={(e) => set('radiusPx', Number(e.target.value))} />
                </Field>
                <Field label="Form width (px)">
                  <Input type="number" min={320} max={1200} value={tokens.maxWidthPx}
                         onChange={(e) => set('maxWidthPx', Number(e.target.value))} />
                </Field>
              </div>
            </Section>

            <Section
              title="Contrast"
              hint="Text pairs must reach 4.5:1 to be saved. Non-text pairs are advisory."
            >
              <ul className="space-y-2">
                {(checked?.contrast ?? []).map((pair) => (
                  <li key={pair.label} className="flex items-center gap-3 text-sm">
                    <Icon
                      name={pair.passes ? 'check' : 'alert'}
                      size={15}
                      className={pair.passes ? 'text-[var(--ff-success)]' : 'text-[var(--ff-danger)]'}
                    />
                    <span className="flex-1">{pair.label}</span>
                    <span className={`tabular-nums ${pair.passes ? '' : 'font-semibold text-[var(--ff-danger)]'}`}>
                      {pair.ratio.toFixed(2)}:1
                    </span>
                    <span className="w-20 text-right text-xs text-[var(--ff-text-muted)]">
                      needs {pair.required}:1
                      {!pair.blocking && ' *'}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-[var(--ff-text-muted)]">
                * advisory. A faint divider is a design choice; text nobody can read is not.
              </p>
            </Section>
          </div>

          {/* The preview is the real runtime markup, not an impression of it, so what is
              shown here is what a respondent gets. */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <Section title="Preview">
              <ThemePreview tokens={tokens} />
            </Section>
          </div>
        </div>
      </Stack>
    </Page>
  );
}

function ColourField({
  label, value, onChange,
}: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold">{label}</span>
      <span className="flex gap-2">
        <input
          type="color"
          aria-label={`${label} colour picker`}
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded border
                     border-[var(--ff-border)] bg-transparent p-0.5"
        />
        <input
          value={value}
          aria-label={`${label} hex value`}
          onChange={(e) => onChange(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-[var(--ff-border-strong)]
                     bg-[var(--ff-surface)] px-2.5 py-2 font-mono text-sm"
        />
      </span>
    </label>
  );
}

function ThemePreview({ tokens }: { tokens: ThemeTokensDto }) {
  // Applied as inline custom properties on a wrapper. Values come straight from the
  // editor's own state — the server refuses anything unsafe on save, and nothing here is
  // persisted or shown to anybody else.
  const style = {
    background: tokens.page,
    '--p': tokens.primary,
    '--on-p': tokens.onPrimary,
    '--s': tokens.surface,
    '--t': tokens.text,
    '--tm': tokens.textMuted,
    '--b': tokens.border,
    '--r': `${tokens.radiusPx}px`,
    '--f': tokens.fontFamily,
    '--fs': `${tokens.fontSizePx}px`,
  } as React.CSSProperties;

  return (
    <div className="rounded-lg p-4" style={style}>
      <div
        className="rounded-[var(--r)] p-4"
        style={{ background: 'var(--s)', color: 'var(--t)', fontFamily: 'var(--f)', fontSize: 'var(--fs)' }}
      >
        <div className="text-lg font-semibold">Customer feedback</div>
        <p className="mt-1 text-sm" style={{ color: 'var(--tm)' }}>
          Two minutes, five questions.
        </p>

        <label className="mt-4 block text-sm font-medium">Your name</label>
        <input
          readOnly
          value="Ana Silva"
          className="mt-1 w-full px-3 py-2"
          style={{
            background: 'var(--s)', color: 'var(--t)',
            border: '1px solid var(--b)', borderRadius: 'var(--r)',
          }}
        />

        <button
          type="button"
          className="mt-4 px-4 py-2 text-sm font-semibold"
          style={{ background: 'var(--p)', color: 'var(--on-p)', borderRadius: 'var(--r)' }}
        >
          Submit
        </button>
      </div>
    </div>
  );
}
